import { InlineAlert } from '@ai-tutor/ui';
import { Loader2 } from 'lucide-react';

import type { useProrationPreview } from '@/hooks/account/useBilling';
import { ApiError, type Plan, type ProrationPreview } from '@/lib/api-client';
import {
  featuresLostOnDowngrade,
  formatUsd,
  planDisplayName,
  type ChangeDirection,
} from '@/lib/billing';
import { parseUtcDate } from '@/lib/datetime';

/**
 * What a plan change will do, in words and dates, before the user confirms.
 * One variant per direction `classifyChange` can return.
 */

const DATE_FORMAT: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
};

export function formatPeriodDate(iso: string): string {
  return parseUtcDate(iso).toLocaleDateString(undefined, DATE_FORMAT);
}

function formatUnixDate(unix: number): string {
  return new Date(unix * 1000).toLocaleDateString(undefined, DATE_FORMAT);
}

function formatMoney(cents: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format(cents / 100);
}

const PREVIEW_FALLBACK =
  "Couldn't load the price for this change. Try again in a moment.";

/**
 * The preview box's copy for a failed request.
 *
 * Was `error.message` verbatim, and for a 5xx that is the transport string
 * the API client builds — a user choosing a plan read "HTTP 500: Internal
 * Server Error". A 4xx keeps its detail: the route raises those with
 * user-facing sentences ("Resume it before scheduling a plan change").
 */
function previewErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const clientError = error.status >= 400 && error.status < 500;
    return clientError && error.detail ? error.detail : PREVIEW_FALLBACK;
  }
  return PREVIEW_FALLBACK;
}

/** A display name kept on one line — "Plus ·" / "Monthly" split badly. */
function PlanName({ children }: { children: string }) {
  return <strong className='whitespace-nowrap'>{children}</strong>;
}

export interface ChangePlanSummaryProps {
  direction: ChangeDirection;
  proration: ReturnType<typeof useProrationPreview>;
  currentPlan: Plan;
  targetPlan: Plan;
  periodEnd: string;
}

export function ChangePlanSummary({
  direction,
  proration,
  currentPlan,
  targetPlan,
  periodEnd,
}: ChangePlanSummaryProps) {
  if (direction === 'noop') {
    return (
      <p
        data-testid='change-summary-none'
        className='rounded-lg bg-muted p-4 text-sm text-muted-foreground'
      >
        You&apos;re on <PlanName>{planDisplayName(currentPlan)}</PlanName>. Pick
        a different plan to see what changes and when.
      </p>
    );
  }
  if (direction === 'upgrade_immediate') {
    return <UpgradeSummary proration={proration} targetPlan={targetPlan} />;
  }
  return (
    <DowngradeSummary
      toFree={direction === 'downgrade_to_free'}
      currentPlan={currentPlan}
      targetPlan={targetPlan}
      periodEnd={formatPeriodDate(periodEnd)}
    />
  );
}

function DowngradeSummary({
  toFree,
  currentPlan,
  targetPlan,
  periodEnd,
}: {
  toFree: boolean;
  currentPlan: Plan;
  targetPlan: Plan;
  periodEnd: string;
}) {
  const current = planDisplayName(currentPlan);
  const target = planDisplayName(targetPlan);
  const lost = toFree ? featuresLostOnDowngrade(currentPlan, targetPlan) : [];
  return (
    <div
      data-testid='downgrade-summary'
      className='space-y-2 rounded-lg bg-muted p-4 text-sm text-foreground'
    >
      {toFree ? (
        <p>
          You keep <PlanName>{current}</PlanName> until{' '}
          <strong>{periodEnd}</strong>, then move to{' '}
          <PlanName>{target}</PlanName>. No charge today.
        </p>
      ) : (
        <p>
          You keep <PlanName>{current}</PlanName> until{' '}
          <strong>{periodEnd}</strong>. From {periodEnd} you&apos;ll be on{' '}
          <PlanName>{target}</PlanName> at{' '}
          <strong>
            {formatUsd(targetPlan.amount_cents)}/{targetPlan.interval}
          </strong>
          , first charged {periodEnd}. No charge today.
        </p>
      )}
      {toFree &&
        (lost.length > 0 ? (
          <div>
            <p>On {periodEnd} you&apos;ll lose:</p>
            <ul
              data-testid='features-lost'
              className='mt-1 list-disc space-y-0.5 pl-5 text-muted-foreground'
            >
              {lost.map(f => (
                <li key={f}>{f}</li>
              ))}
            </ul>
          </div>
        ) : (
          <p>
            On {periodEnd} you&apos;ll lose access to {currentPlan.name}{' '}
            features.
          </p>
        ))}
      <p className='text-xs text-muted-foreground'>
        You can undo this any time before then.
      </p>
    </div>
  );
}

function UpgradeSummary({
  proration,
  targetPlan,
}: {
  proration: ReturnType<typeof useProrationPreview>;
  targetPlan: Plan;
}) {
  if (proration.isLoading) {
    return (
      <div className='rounded-lg border border-border p-4'>
        <Loader2
          aria-label='Loading the price'
          className='h-4 w-4 animate-spin text-muted-foreground'
        />
      </div>
    );
  }
  if (proration.isError) {
    return (
      <div data-testid='proration-error'>
        <InlineAlert
          tone='error'
          message={previewErrorMessage(proration.error)}
        />
      </div>
    );
  }
  if (!proration.data) return null;
  return <NextInvoice preview={proration.data} targetPlan={targetPlan} />;
}

/**
 * What the upgrade charges, read from Stripe's own preview of the exact change
 * the confirm button makes — nothing here is assumed about proration.
 *
 * On the product path (checkout Monthly, then Change plan → Yearly) Stripe
 * charges AT the switch: an interval change resets the billing anchor, so the
 * invoice is the credit for the unused monthly time, the yearly price and the
 * XONERA coupon, and the yearly period starts now. The backend flags that as
 * `charged_now`. The other branch — prorations riding on the next invoice —
 * is kept for a change that keeps the anchor.
 */
function NextInvoice({
  preview,
  targetPlan,
}: {
  preview: ProrationPreview;
  targetPlan: Plan;
}) {
  const money = (cents: number, currency = preview.currency) =>
    formatMoney(cents, currency);
  return (
    <div
      data-testid='proration-panel'
      data-charged-now={preview.charged_now ? 'true' : 'false'}
      data-amount-due-cents={preview.amount_due}
      className='space-y-2 rounded-lg bg-muted p-4 text-sm text-foreground'
    >
      <p>
        You switch to <PlanName>{planDisplayName(targetPlan)}</PlanName> now.{' '}
        {preview.charged_now ? (
          <>
            You&apos;ll be charged{' '}
            <strong data-testid='proration-amount'>
              {money(preview.amount_due_now_cents)}
            </strong>{' '}
            today{preview.lines.length > 0 ? ':' : '.'}
          </>
        ) : (
          <strong>No charge today.</strong>
        )}
      </p>
      {!preview.charged_now && (
        <p>
          {preview.next_payment_attempt
            ? `On ${formatUnixDate(preview.next_payment_attempt)}`
            : 'On your next invoice'}{' '}
          you&apos;ll be billed{' '}
          <strong data-testid='proration-amount'>
            {money(preview.amount_due)}
          </strong>
          {preview.lines.length > 0 ? ':' : '.'}
        </p>
      )}
      {preview.lines.length > 0 && (
        <ul data-testid='proration-lines' className='space-y-1'>
          {preview.lines.map((line, i) => (
            <li
              key={`${line.description ?? 'line'}-${i}`}
              data-line-kind={line.kind}
              className='flex items-baseline justify-between gap-4 text-xs text-muted-foreground'
            >
              <span>{line.description ?? 'Adjustment'}</span>
              <span className='shrink-0 font-mono tabular-nums text-foreground'>
                {money(line.amount, line.currency || preview.currency)}
              </span>
            </li>
          ))}
        </ul>
      )}
      <RenewalLine preview={preview} targetPlan={targetPlan} />
    </div>
  );
}

/** "Your yearly plan runs until Sep 29, 2027, then renews at $155.88/year." */
function RenewalLine({
  preview,
  targetPlan,
}: {
  preview: ProrationPreview;
  targetPlan: Plan;
}) {
  const runsUntil = preview.charged_now
    ? preview.new_period_end
    : preview.renews_at;
  if (!runsUntil || preview.renewal_amount_cents === null) return null;
  const cadence = targetPlan.interval === 'year' ? 'yearly' : 'monthly';
  return (
    <p data-testid='proration-renewal' className='text-muted-foreground'>
      Your {cadence} plan runs until{' '}
      <strong className='text-foreground'>{formatUnixDate(runsUntil)}</strong>,
      then renews at{' '}
      {formatMoney(preview.renewal_amount_cents, preview.currency)}/
      {targetPlan.interval}.
    </p>
  );
}
